import { useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { MerchantTerminalsContext } from '../../context/merchantTerminals/MerchantTerminalContext';
import { MerchantTerminal } from '../../models/interfaces';
import Message from '../../components/ui/Message';
import FormModal from '../../components/ui/FormModal';
import { useRequiredFields } from '../../hooks/useRequiredFields';

export const TERMINAL_REGEX = /^\d{1,10}$/;   // hasta 10 dígitos

const currencyBadge: Record<number, string> = {
    188: 'bg-primary-subtle text-primary-emphasis',
    840: 'bg-success-subtle text-success-emphasis',
};

const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleString('es-CR', { dateStyle: 'short', timeStyle: 'short' }) : '-';

// Terminales del comercio (banco Swiftpay): número de hasta 10 dígitos por moneda. El borrado es lógico.
export const MerchantTerminalsPage = () => {
    const { state } = useLocation();
    const merchant = state?.merchant;
    const navigate = useNavigate();

    const { terminals, currencies, isLoading, loadTerminals, saveTerminal, deleteTerminal } = useContext(MerchantTerminalsContext);
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState<Partial<MerchantTerminal> | null>(null);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [error, setError] = useState('');
    const [toDelete, setToDelete] = useState<MerchantTerminal | null>(null);
    const required = useRequiredFields();

    useEffect(() => {
        if (!merchant) return;
        loadTerminals(merchant.id).then(resp => { if (resp !== 'success') setError(resp); });
    }, [merchant?.id, loadTerminals]);

    const currencyName = (currencyId: number) =>
        currencies.find(currency => Number(currency.code) === Number(currencyId))?.description ?? String(currencyId);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return terminals;
        return terminals.filter(terminal =>
            [terminal.terminalNumber, terminal.description, currencyName(terminal.currencyId)]
                .some(v => String(v ?? '').toLowerCase().includes(term)));
    }, [terminals, search, currencies]);

    // Sin comercio (ej. acceso directo a la URL) se vuelve al listado
    if (!merchant) return <Navigate to="/merchants" replace />;

    const openForm = (terminal?: MerchantTerminal) => {
        required.reset();
        setFormError('');
        setEditing(terminal
            ? { ...terminal }
            : { merchantId: merchant.id, terminalNumber: '', currencyId: currencies.length === 1 ? Number(currencies[0].code) : undefined, description: '' });
    };

    const update = (field: keyof MerchantTerminal, value: string | number | undefined) => setEditing(prev => ({ ...prev, [field]: value }));

    const numberInvalid = !TERMINAL_REGEX.test(editing?.terminalNumber ?? '');

    const handleSave = async () => {
        if (!editing) return;
        if (numberInvalid || !editing.currencyId) {
            required.fail();
            setFormError(numberInvalid ? 'El número de terminal debe tener solo dígitos, máximo 10.' : 'Debe completar los campos requeridos.');
            return;
        }
        setSaving(true);
        const resp = await saveTerminal({ ...editing, merchantId: merchant.id });
        setSaving(false);
        if (resp === 'success') setEditing(null);
        else setFormError(resp);
    };

    const processDelete = async () => {
        const terminal = toDelete;
        setToDelete(null);
        if (!terminal?.id) return;
        const resp = await deleteTerminal(terminal.id);
        if (resp !== 'success') setError(resp);
    };

    return (
        <div className='mt-5 container-fluid px-4' style={{ flex: 1, marginTop: 10 }}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Terminales</h3>
                    <p className="text-muted mb-0">Terminales por moneda del comercio <span className="fw-semibold text-body">{merchant.name}</span>.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/merchants')}>
                        <i className="bi bi-arrow-left me-1" /> Volver a comercios
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => openForm()}>
                        <i className="bi bi-plus-lg me-1" /> Agregar terminal
                    </button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input className="form-control" type="text" placeholder="Buscar por terminal, moneda o descripción..."
                                    value={search} onChange={(e) => setSearch(e.target.value)} />
                                {search && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setSearch('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='table-responsive'>
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Terminal</th>
                                    <th>Moneda</th>
                                    <th>Descripción</th>
                                    <th>Creada</th>
                                    <th>Actualizada</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={6} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : filtered.length > 0 ? filtered.map(terminal => (
                                    <tr key={terminal.id}>
                                        <td className="fw-medium" style={{ fontFamily: 'monospace' }}>{terminal.terminalNumber}</td>
                                        <td>
                                            <span className={`badge rounded-pill ${currencyBadge[terminal.currencyId] ?? 'bg-secondary-subtle text-secondary-emphasis'}`}>
                                                {currencyName(terminal.currencyId)}
                                            </span>
                                        </td>
                                        <td className="text-muted">{terminal.description || '-'}</td>
                                        <td className="text-muted small">{formatDate(terminal.createdAt)}</td>
                                        <td className="text-muted small">{formatDate(terminal.updatedAt)}</td>
                                        <td style={{ textAlign: 'center', width: 110, whiteSpace: 'nowrap' }}>
                                            <button type="button" className="btn btn-sm btn-primary" title="Editar terminal" onClick={() => openForm(terminal)}>
                                                <i className="bi bi-pencil" />
                                            </button>
                                            <button type="button" className="btn btn-sm btn-danger ms-1" title="Eliminar terminal" onClick={() => setToDelete(terminal)}>
                                                <i className="bi bi-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={6} className='text-center py-4 text-muted'>
                                        {search ? 'No se encontraron terminales para la búsqueda.' : 'No hay terminales configuradas.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {editing && (
                <FormModal
                    title={editing.id ? 'Editar terminal' : 'Agregar terminal'}
                    onClose={() => setEditing(null)}
                    onSave={handleSave}
                    saving={saving}
                    error={formError}
                >
                    <div className="row g-3 mb-3">
                        <div className="col-md-6">
                            <label htmlFor="terminalNumber" className="form-label">Número de terminal *</label>
                            <input id="terminalNumber" className={`form-control${required.mark(numberInvalid)}`} inputMode="numeric"
                                maxLength={10} autoFocus style={{ fontFamily: 'monospace' }}
                                value={editing.terminalNumber ?? ''} onChange={(e) => update('terminalNumber', e.target.value.replace(/\D/g, ''))} />
                            <div className="invalid-feedback">Solo dígitos, máximo 10</div>
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="terminalCurrency" className="form-label">Moneda *</label>
                            <select id="terminalCurrency" className={`form-select${required.mark(!editing.currencyId)}`} value={editing.currencyId ?? ''}
                                onChange={(e) => update('currencyId', e.target.value ? Number(e.target.value) : undefined)}>
                                <option value="">Seleccione la moneda</option>
                                {currencies.map(currency => <option key={currency.code} value={currency.code}>{currency.description}</option>)}
                            </select>
                            <div className="invalid-feedback">Campo requerido</div>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="terminalDescription" className="form-label">Descripción</label>
                        <input id="terminalDescription" className="form-control" maxLength={200} placeholder="Opcional"
                            value={editing.description ?? ''} onChange={(e) => update('description', e.target.value)} />
                    </div>
                </FormModal>
            )}

            {error && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}

            {toDelete && (
                <Message
                    type="success"
                    title="Atención"
                    message={`¿Está seguro que desea eliminar la terminal ${toDelete.terminalNumber}?`}
                    closeDialog={() => setToDelete(null)}
                    showCancel={true}
                    handleCancel={() => setToDelete(null)}
                    handleOk={processDelete}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
        </div>
    );
};
